import { zCurrency } from "@redotech/money/usd-amounts";
import { privacy } from "@redotech/schema-privacy";
import { DateFromDateOrIsoStringSchema } from "@redotech/zod-util/date-schema";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { SurchargeCategory } from "./outbound-label-enums";

export enum SurchargeSource {
  // Easypost is USPS
  Easypost = "easypost",
  Amazon = "amazon",
  Fedex = "fedex",
  Unishippers = "ups",
  UpsDirect = "ups-direct",
  IB = "international-bridge",
  FirstMile = "first-mile",
  Usps = "usps",
  Dhl = "dhl",
}

export const surchargeCategoryLabels: Record<SurchargeCategory, string> = {
  [SurchargeCategory.Weight]: "Weight",
  [SurchargeCategory.Dimensions]: "Dimensions",
  [SurchargeCategory.FuelSurcharge]: "Fuel Surcharge",
  [SurchargeCategory.DeliveryArea]: "Delivery Area",
  [SurchargeCategory.International]: "International",
  [SurchargeCategory.AddressCorrection]: "Address Correction",
  [SurchargeCategory.PackageType]: "Package Type",
  [SurchargeCategory.Other]: "Other",
};

export const getBaseUniqueIdentifier = (
  source: SurchargeSource,
  shipmentId: string,
) => {
  return source + ":" + shipmentId + ":";
};

export const getDhlUniqueIdentifier = (
  trackingCode: string,
  description: string,
) => {
  return (
    getBaseUniqueIdentifier(SurchargeSource.Dhl, trackingCode) + description
  );
};

// EasyPost (USPS) price adjustments have an id
export const getEasypostUniqueIdentifier = (
  shipmentId: string,
  paylogId: string,
) => {
  return (
    getBaseUniqueIdentifier(SurchargeSource.Easypost, shipmentId) + paylogId
  );
};

// Amazon price adjustment descriptions should be unique per shipment
export const getAmazonUniqueIdentifier = (
  shipmentId: string,
  description: string,
) => {
  return (
    getBaseUniqueIdentifier(SurchargeSource.Amazon, shipmentId) + description
  );
};

export const getFedexUniqueIdentifier = (
  shipmentId: string,
  invoiceNumber?: string,
  description?: string,
) => {
  let id = getBaseUniqueIdentifier(SurchargeSource.Fedex, shipmentId);
  if (invoiceNumber) {
    id += invoiceNumber;
  }
  if (description) {
    id += ":" + description;
  }
  return id;
};

// UPS price adjustment descriptions should be unique and shipment id
export const getUpsUniqueIdentifier = (
  shipmentId: string,
  description: string,
) => {
  return (
    getBaseUniqueIdentifier(SurchargeSource.Unishippers, shipmentId) +
    description
  );
};

export const getIBUniqueIdentifier = (
  trackingNumber: string,
  comment: string,
) => {
  return getBaseUniqueIdentifier(SurchargeSource.IB, trackingNumber) + comment;
};

export const getFirstMileUniqueIdentifier = (
  trackingNumber: string,
  description: string,
) => {
  return (
    getBaseUniqueIdentifier(SurchargeSource.FirstMile, trackingNumber) +
    description
  );
};

export const getUpsDirectUniqueIdentifier = (
  trackingNumber: string,
  description: string,
) => {
  return (
    getBaseUniqueIdentifier(SurchargeSource.UpsDirect, trackingNumber) +
    description
  );
};

export const getUpsDirectInvoiceEventUniqueIdentifier = (
  trackingNumber: string,
  invoiceReferenceId: string,
  eventKey: string,
) => {
  return (
    getBaseUniqueIdentifier(SurchargeSource.UpsDirect, trackingNumber) +
    invoiceReferenceId +
    ":" +
    eventKey
  );
};

export const getUpsDirectAccountLevelUniqueIdentifier = (
  invoiceDate: string,
  billToAccount: string,
  description: string,
) => {
  return (
    getBaseUniqueIdentifier(SurchargeSource.UpsDirect, invoiceDate) +
    billToAccount +
    ":" +
    description
  );
};

export const getUspsUniqueIdentifier = (
  trackingNumber: string,
  postageVariance: number,
) => {
  return (
    getBaseUniqueIdentifier(SurchargeSource.Usps, trackingNumber) +
    postageVariance.toString()
  );
};

export const surchargeSchema_ = z.object({
  teamId: z.string().meta({ privacy: privacy.nonPii }),
  source: createEnum(SurchargeSource, { enumName: "SurchargeSource" }).meta({
    privacy: privacy.nonPii,
  }),
  uniqueIdentifier: z.string().meta({ privacy: privacy.id.external.resource }),
  fulfillmentGroupId: z.string().nullish().meta({ privacy: privacy.nonPii }),
  outboundShipmentId: z.string().nullish().meta({ privacy: privacy.nonPii }),
  outboundShipmentPackageId: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  shipmentId: z.string().nullish().meta({ privacy: privacy.id.tracking }),
  trackingNumber: z.string().nullish().meta({ privacy: privacy.id.tracking }),
  date: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }),
  amount: z.string().meta({ privacy: privacy.nonPii }),
  currency: zCurrency.meta({ privacy: privacy.nonPii }),
  description: z.string().meta({ privacy: privacy.text.thirdParty }),
  category: createEnum(SurchargeCategory, { enumName: "SurchargeCategory" })
    .default(SurchargeCategory.Other)
    .meta({ privacy: privacy.nonPii }),
  loadedAt: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }),
  capturedDimensions: z
    .object({
      lengthInInches: z.number().nullish().meta({ privacy: privacy.nonPii }),
      widthInInches: z.number().nullish().meta({ privacy: privacy.nonPii }),
      heightInInches: z.number().nullish().meta({ privacy: privacy.nonPii }),
      weightInOunces: z.number().nullish().meta({ privacy: privacy.nonPii }),
      packageType: z.string().nullish().meta({ privacy: privacy.nonPii }),
    })
    .nullish()
    .meta({ privacy: privacy.structured }),
});

export const SurchargeSchema: z.ZodType<Surcharge> = surchargeSchema_;
export interface Surcharge extends z.infer<typeof surchargeSchema_> {}

export function getQualifyingAlarmingSurcharges(
  surcharges: Surcharge[],
  selectedCategories: SurchargeCategory[],
  threshold: number | null | undefined,
): Surcharge[] {
  return surcharges.filter((surcharge) => {
    const categoryMatches =
      selectedCategories.length === 0 ||
      selectedCategories.includes(surcharge.category);
    const amountMatches =
      threshold == null || Math.abs(Number(surcharge.amount)) > threshold;
    return categoryMatches && amountMatches;
  });
}

const surchargeOwnerColumns = {
  FULFILLMENT_GROUP_ID: z.string().nullish().meta({ privacy: privacy.nonPii }),
  OUTBOUND_SHIPMENT_ID: z.coerce
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  OUTBOUND_SHIPMENT_PACKAGE_ID: z.coerce
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
};

const amazonAdjustmentSnowflakeResponseSchema_ = z.object({
  ...surchargeOwnerColumns,
  SHIPMENT_ID: z.string().nullish().meta({ privacy: privacy.nonPii }),
  TRACKING_ID: z.string().meta({ privacy: privacy.id.tracking }),
  BASE_RATE_INCLUDING_TAX_AMOUNT_USD: z
    .number()
    .meta({ privacy: privacy.nonPii }),
  COMMENTS: z.string().nullish().meta({ privacy: privacy.nonPii }),
  TEAM: z.string().meta({ privacy: privacy.nonPii }),
  DATE_OF_CHARGE: z.date().meta({ privacy: privacy.nonPii }),
  LOADED_AT: z.date().meta({ privacy: privacy.nonPii }),
  ASSIGNED_FULFILLMENT_SERVICE: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
});
export const AmazonAdjustmentSnowflakeResponseSchema: z.ZodType<AmazonAdjustmentSnowflakeResponse> =
  amazonAdjustmentSnowflakeResponseSchema_;
export interface AmazonAdjustmentSnowflakeResponse extends z.infer<
  typeof amazonAdjustmentSnowflakeResponseSchema_
> {}

const fedexSurchargeSnowflakeResponseSchema_ = z.object({
  ...surchargeOwnerColumns,
  INVOICE_DATE: z.date().meta({ privacy: privacy.nonPii }),
  INVOICE_NUMBER: z.string().meta({ privacy: privacy.nonPii }),
  EXPRESS_OR_GROUND_TRACKING_ID: z
    .string()
    .meta({ privacy: privacy.id.tracking }),
  CONCATENATED_TRACKING_CODE: z.string().meta({ privacy: privacy.id.tracking }),
  TRANSPORTATION_CHARGE_AMOUNT: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  NET_CHARGE_AMOUNT: z.number().meta({ privacy: privacy.nonPii }),
  MAX_PRIOR_FULL_INVOICE_NET_CHARGE_AMOUNT: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  FULL_INVOICE_SEQUENCE: z.number().nullish().meta({ privacy: privacy.nonPii }),
  ACTUAL_WEIGHT_IN_POUNDS: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  DIM_LENGTH_IN_INCHES: z.number().nullish().meta({ privacy: privacy.nonPii }),
  DIM_WIDTH_IN_INCHES: z.number().nullish().meta({ privacy: privacy.nonPii }),
  DIM_HEIGHT_IN_INCHES: z.number().nullish().meta({ privacy: privacy.nonPii }),
  REPORTED_LENGTH: z.number().nullish().meta({ privacy: privacy.nonPii }),
  REPORTED_WIDTH: z.number().nullish().meta({ privacy: privacy.nonPii }),
  REPORTED_HEIGHT: z.number().nullish().meta({ privacy: privacy.nonPii }),
  REPORTED_WEIGHT_OZ: z.number().nullish().meta({ privacy: privacy.nonPii }),
  LOADED_AT: z.date().meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_1: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_1: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_2: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_2: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_3: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_3: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_4: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_4: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_5: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_5: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_6: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_6: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_7: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_7: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_8: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_8: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_9: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_9: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_10: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_10: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_11: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_11: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_12: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_12: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_13: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_13: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_14: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_14: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_15: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_15: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_16: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_16: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_17: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_17: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_18: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_18: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_19: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_19: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_20: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_20: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_21: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_21: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_22: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_22: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_23: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_23: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_24: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_24: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_25: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_25: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_26: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_26: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_27: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_27: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_28: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_28: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_29: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_29: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_30: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_30: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_31: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_31: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_32: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_32: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_33: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_33: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_34: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_34: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_35: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_35: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_36: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_36: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_37: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_37: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_38: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_38: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_39: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_39: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_40: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_40: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_41: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_41: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_42: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_42: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_43: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_43: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_44: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_44: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_45: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_45: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_46: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_46: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_47: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_47: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_48: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_48: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_49: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_49: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_AMOUNT_50: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TRACKING_ID_CHARGE_DESCRIPTION_50: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  TEAM: z.string().meta({ privacy: privacy.nonPii }),
  ASSIGNED_FULFILLMENT_SERVICE: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  PROVIDER_COST: z.number().meta({ privacy: privacy.nonPii }),
});
export const FedexSurchargeSnowflakeResponseSchema: z.ZodType<FedexSurchargeSnowflakeResponse> =
  fedexSurchargeSnowflakeResponseSchema_;
export interface FedexSurchargeSnowflakeResponse extends z.infer<
  typeof fedexSurchargeSnowflakeResponseSchema_
> {}

//USPS
const easypostSurchargeSnowflakeResponseSchema_ = z.object({
  ...surchargeOwnerColumns,
  INVOICE_DATE: z.date().meta({ privacy: privacy.nonPii }),
  SHIPMENT_ID: z.string().nullish().meta({ privacy: privacy.nonPii }),
  TRACKING_CODE: z.string().nullish().meta({ privacy: privacy.id.tracking }),
  ADJUSTMENT_AMOUNT: z.number().meta({ privacy: privacy.nonPii }),
  ADJUSTMENT_REASON: z.string().nullish().meta({ privacy: privacy.nonPii }),
  CAPTURED_PARCEL_LENGTH: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  CAPTURED_PARCEL_WIDTH: z.number().nullish().meta({ privacy: privacy.nonPii }),
  CAPTURED_PARCEL_HEIGHT: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  PARCEL_WEIGHT: z.number().nullish().meta({ privacy: privacy.nonPii }),
  INVOICE_PAYMENT_LOG: z.string().meta({ privacy: privacy.nonPii }),
  TEAM: z.string().meta({ privacy: privacy.nonPii }),
  ASSIGNED_FULFILLMENT_SERVICE: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
});
export const EasypostSurchargeSnowflakeResponseSchema: z.ZodType<EasypostSurchargeSnowflakeResponse> =
  easypostSurchargeSnowflakeResponseSchema_;
export interface EasypostSurchargeSnowflakeResponse extends z.infer<
  typeof easypostSurchargeSnowflakeResponseSchema_
> {}

const upsAdjustmentSnowflakeResponseSchema_ = z.object({
  ...surchargeOwnerColumns,
  TEAM_ID: z.string().meta({ privacy: privacy.nonPii }),
  ASSIGNED_FULFILLMENT_SERVICE: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  QUOTED_COST: z.number().meta({ privacy: privacy.nonPii }),
  TRACKING_CODE: z.string().meta({ privacy: privacy.id.tracking }),
  TOTAL_CHARGE_AMOUNT: z.number().meta({ privacy: privacy.nonPii }),
  CHARGES: z
    .array(
      z
        .object({
          charge_amount: z.coerce.number().meta({ privacy: privacy.nonPii }),
          charge_type: z.string().nullish().meta({ privacy: privacy.nonPii }),
          invoice_date: z.string().meta({ privacy: privacy.nonPii }),
          original_weight: z.coerce
            .string()
            .nullish()
            .meta({ privacy: privacy.nonPii }),
          charged_weight: z.coerce
            .string()
            .nullish()
            .meta({ privacy: privacy.nonPii }),
          charge_number: z.number().meta({ privacy: privacy.nonPii }),
        })
        .meta({ privacy: privacy.structured }),
    )
    .meta({ privacy: privacy.structured }),
  LOADED_AT: z.date().meta({ privacy: privacy.nonPii }),
  SURCHARGE: z.number().meta({ privacy: privacy.nonPii }),
});
export const UpsAdjustmentSnowflakeResponseSchema: z.ZodType<UnishippersAdjustmentSnowflakeResponse> =
  upsAdjustmentSnowflakeResponseSchema_;
export interface UnishippersAdjustmentSnowflakeResponse extends z.infer<
  typeof upsAdjustmentSnowflakeResponseSchema_
> {}

const ibAdjustmentSnowflakeResponseSchema_ = z.object({
  ...surchargeOwnerColumns,
  ASSIGNED_FULFILLMENT_SERVICE: z.string().meta({ privacy: privacy.nonPii }),
  ADDITIONAL_POSTAGE_DUE: z.number().meta({ privacy: privacy.nonPii }),
  COMMENT: z.string().meta({ privacy: privacy.nonPii }),
  TRACKING_NUMBER: z.string().meta({ privacy: privacy.id.tracking }),
  RECALCULATED_POSTAGE: z.number().meta({ privacy: privacy.nonPii }),
  DATE_OF_SAMPLING: z.date().meta({ privacy: privacy.nonPii }),
  WEIGHT: z.number().meta({ privacy: privacy.nonPii }),
  WEIGHT_UNIT: z.string().meta({ privacy: privacy.nonPii }),
  LENGTH: z.number().meta({ privacy: privacy.nonPii }),
  WIDTH: z.number().meta({ privacy: privacy.nonPii }),
  HEIGHT: z.number().meta({ privacy: privacy.nonPii }),
  DIMENSIONS_UNIT: z.string().meta({ privacy: privacy.nonPii }),
});
export const IbAdjustmentSnowflakeResponseSchema: z.ZodType<IbAdjustmentSnowflakeResponse> =
  ibAdjustmentSnowflakeResponseSchema_;
export interface IbAdjustmentSnowflakeResponse extends z.infer<
  typeof ibAdjustmentSnowflakeResponseSchema_
> {}

const firstMileSurchargeSnowflakeResponseSchema_ = z.object({
  ...surchargeOwnerColumns,
  TEAM: z.string().nullish().meta({ privacy: privacy.nonPii }),
  ASSIGNED_FULFILLMENT_SERVICE: z.string().meta({ privacy: privacy.nonPii }),
  INVOICE_DATE: z.date().meta({ privacy: privacy.nonPii }),
  INVOICE_NUMBER: z.string().nullish().meta({ privacy: privacy.nonPii }),
  TRACKING_NUMBER: z.string().meta({ privacy: privacy.id.tracking }),
  SHIPMENT_ID: z.string().nullish().meta({ privacy: privacy.nonPii }),
  TOTAL_CHARGE: z.number().nullish().meta({ privacy: privacy.nonPii }),
  FM_BASE_RATE: z.number().nullish().meta({ privacy: privacy.nonPii }),
  ZONE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  WEIGHT_OZ: z.number().nullish().meta({ privacy: privacy.nonPii }),
  WEIGHT_LBS: z.number().nullish().meta({ privacy: privacy.nonPii }),
  FUEL: z.number().nullish().meta({ privacy: privacy.nonPii }),
  DIM_LENGTH: z.number().nullish().meta({ privacy: privacy.nonPii }),
  DIM_WIDTH: z.number().nullish().meta({ privacy: privacy.nonPii }),
  DIM_HEIGHT: z.number().nullish().meta({ privacy: privacy.nonPii }),
  ACTUAL_WEIGHT: z.number().nullish().meta({ privacy: privacy.nonPii }),
  LOADED_AT: z.date().meta({ privacy: privacy.nonPii }),
  SURCHARGE_1_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_1: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_2_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_2: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_3_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_3: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_4_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_4: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_5_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_5: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_6_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_6: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_7_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_7: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_8_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_8: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_9_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_9: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_10_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_10: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_11_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_11: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_12_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_12: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_13_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_13: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_14_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_14: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_15_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_15: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_16_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_16: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_17_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_17: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_18_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_18: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_19_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_19: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_20_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_20: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_21_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_21: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_22_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_22: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_23_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_23: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_24_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_24: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_25_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_25: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_26_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_26: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_27_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SURCHARGE_27: z.number().nullish().meta({ privacy: privacy.nonPii }),
  // Will we get 27 surcharges? Probably not, but that is the schema
});
export const FirstMileSurchargeSnowflakeResponseSchema: z.ZodType<FirstMileSurchargeSnowflakeResponse> =
  firstMileSurchargeSnowflakeResponseSchema_;
export interface FirstMileSurchargeSnowflakeResponse extends z.infer<
  typeof firstMileSurchargeSnowflakeResponseSchema_
> {}

const upsDirectLabelSurchargeSnowflakeResponseSchema_ = z.object({
  ...surchargeOwnerColumns,
  SERVICE_NAME: z.string().nullish().meta({ privacy: privacy.nonPii }),
  TRACKING_NUMBER: z.string().meta({ privacy: privacy.id.tracking }),
  INVOICE_REFERENCE_ID: z.string().nullish().meta({ privacy: privacy.nonPii }),
  INVOICE_SEQUENCE: z.number().nullish().meta({ privacy: privacy.nonPii }),
  INVOICE_DATE: z.date().nullish().meta({ privacy: privacy.nonPii }),
  CURRENCY: z.string().nullish().meta({ privacy: privacy.nonPii }),
  TOTAL_COST: z.number().nullish().meta({ privacy: privacy.nonPii }),
  FREIGHT_CHARGE: z.number().nullish().meta({ privacy: privacy.nonPii }),
  FUEL_CHARGE: z.number().nullish().meta({ privacy: privacy.nonPii }),
  SHIPMENT_WEIGHT: z.coerce
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  SHIPMENT_WEIGHT_UNIT: z.string().nullish().meta({ privacy: privacy.nonPii }),
  SHIPMENT_HEIGHT: z.coerce
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  SHIPMENT_WIDTH: z.coerce.number().nullish().meta({ privacy: privacy.nonPii }),
  SHIPMENT_LENGTH: z.coerce
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  SHIPMENT_DIMENSION_UNIT: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  KEY_ENTERED_WEIGHT: z.coerce
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  KEY_ENTERED_WEIGHT_UNIT: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  KEY_ENTERED_HEIGHT: z.coerce
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  KEY_ENTERED_WIDTH: z.coerce
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  KEY_ENTERED_LENGTH: z.coerce
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  DIMENSION_UNIT: z.string().nullish().meta({ privacy: privacy.nonPii }),
  ZONE: z
    .string()
    .or(z.number())
    .transform(String)
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  ADDITIONAL_CHARGE_1_DESC: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  ADDITIONAL_CHARGE_1: z.number().nullish().meta({ privacy: privacy.nonPii }),
  ADDITIONAL_CHARGE_2_DESC: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  ADDITIONAL_CHARGE_2: z.number().nullish().meta({ privacy: privacy.nonPii }),
  ADDITIONAL_CHARGE_3_DESC: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  ADDITIONAL_CHARGE_3: z.number().nullish().meta({ privacy: privacy.nonPii }),
  ADDITIONAL_CHARGE_4_DESC: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  ADDITIONAL_CHARGE_4: z.number().nullish().meta({ privacy: privacy.nonPii }),
  ADDITIONAL_CHARGE_5_DESC: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  ADDITIONAL_CHARGE_5: z.number().nullish().meta({ privacy: privacy.nonPii }),
  ADDITIONAL_CHARGE_6_DESC: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  ADDITIONAL_CHARGE_6: z.number().nullish().meta({ privacy: privacy.nonPii }),
  OTHER_ADDITIONAL_CHARGES: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  OTHER_ADDITIONAL_CHARGES_AMOUNT: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  VAT: z.number().nullish().meta({ privacy: privacy.nonPii }),
  TAX: z.number().nullish().meta({ privacy: privacy.nonPii }),
  DUTY: z.number().nullish().meta({ privacy: privacy.nonPii }),
  BROKERAGE: z.number().nullish().meta({ privacy: privacy.nonPii }),
  GST_TAX_AMT: z.number().nullish().meta({ privacy: privacy.nonPii }),
  HST_TAX_AMT: z.number().nullish().meta({ privacy: privacy.nonPii }),
  PST_TAX_AMT: z.number().nullish().meta({ privacy: privacy.nonPii }),
  QST_TAX_AMT: z.number().nullish().meta({ privacy: privacy.nonPii }),
  GST_HST_TAX_AMT: z.number().nullish().meta({ privacy: privacy.nonPii }),
  LOADED_AT: z.date().meta({ privacy: privacy.nonPii }),
  TEAM: z.string().meta({ privacy: privacy.nonPii }),
  PROVIDER_COST: z.number().meta({ privacy: privacy.nonPii }),
  ASSIGNED_FULFILLMENT_SERVICE: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  IS_DDP: z.boolean().nullish().meta({ privacy: privacy.nonPii }),
});
export const UpsDirectLabelSurchargeSnowflakeResponseSchema: z.ZodType<UpsDirectLabelSurchargeSnowflakeResponse> =
  upsDirectLabelSurchargeSnowflakeResponseSchema_;
export interface UpsDirectLabelSurchargeSnowflakeResponse extends z.infer<
  typeof upsDirectLabelSurchargeSnowflakeResponseSchema_
> {}

const upsDirectAccountLevelSurchargeSnowflakeResponseSchema_ = z.object({
  SERVICE_NAME: z.string().meta({ privacy: privacy.nonPii }),
  BILL_TO_ACCOUNT: z.string().meta({ privacy: privacy.credential }),
  TOTAL_COST: z.number().meta({ privacy: privacy.nonPii }),
  INVOICE_DATE: z.date().meta({ privacy: privacy.nonPii }),
  LOADED_AT: z.date().meta({ privacy: privacy.nonPii }),
});
export const UpsDirectAccountLevelSurchargeSnowflakeResponseSchema: z.ZodType<UpsDirectAccountLevelSurchargeSnowflakeResponse> =
  upsDirectAccountLevelSurchargeSnowflakeResponseSchema_;
export interface UpsDirectAccountLevelSurchargeSnowflakeResponse extends z.infer<
  typeof upsDirectAccountLevelSurchargeSnowflakeResponseSchema_
> {}

// USPS
const uspsAdjustmentSnowflakeResponseSchema_ = z.object({
  ...surchargeOwnerColumns,
  SOURCE_RECORD_TYPE: z.string().nullish().meta({ privacy: privacy.nonPii }),
  UNIQUE_IDENTIFIER: z
    .string()
    .nullish()
    .meta({ privacy: privacy.id.external.resource }),
  INVOICE_LINE_ITEM: z.string().nullish().meta({ privacy: privacy.nonPii }),
  TRACKING_NUMBER: z.string().meta({ privacy: privacy.id.tracking }),
  TEAM: z.string().meta({ privacy: privacy.nonPii }),
  ASSIGNED_FULFILLMENT_SERVICE: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  SURCHARGE_AMOUNT: z.number().nullish().meta({ privacy: privacy.nonPii }),
  POSTAGE_VARIANCE: z.number().nullish().meta({ privacy: privacy.nonPii }),
  ROOT_CAUSE: z
    .array(z.string().meta({ privacy: privacy.nonPii }))
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  SURCHARGE_DATE: z.date().meta({ privacy: privacy.nonPii }),
  LOADED_AT: z.date().meta({ privacy: privacy.nonPii }),
  HEIGHT_CLAIMED: z.string().nullish().meta({ privacy: privacy.nonPii }),
  HEIGHT_ASSESSED: z.string().nullish().meta({ privacy: privacy.nonPii }),
  LENGTH_CLAIMED: z.string().nullish().meta({ privacy: privacy.nonPii }),
  LENGTH_ASSESSED: z.string().nullish().meta({ privacy: privacy.nonPii }),
  WEIGHT_CLAIMED: z.string().nullish().meta({ privacy: privacy.nonPii }),
  WEIGHT_ASSESSED: z.string().nullish().meta({ privacy: privacy.nonPii }),
  WIDTH_CLAIMED: z.string().nullish().meta({ privacy: privacy.nonPii }),
  WIDTH_ASSESSED: z.string().nullish().meta({ privacy: privacy.nonPii }),
});
export const UspsAdjustmentSnowflakeResponseSchema: z.ZodType<UspsAdjustmentSnowflakeResponse> =
  uspsAdjustmentSnowflakeResponseSchema_;
export interface UspsAdjustmentSnowflakeResponse extends z.infer<
  typeof uspsAdjustmentSnowflakeResponseSchema_
> {}

const dhlAdjustmentSnowflakeResponseSchema_ = z.object({
  ...surchargeOwnerColumns,
  TRACKING_CODE: z.string().meta({ privacy: privacy.id.tracking }),
  ASSIGNED_FULFILLMENT_SERVICE: z.string().meta({ privacy: privacy.nonPii }),
  INVOICE_DATE: z.date().meta({ privacy: privacy.nonPii }),
  DIM_HEIGHT: z.number().nullish().meta({ privacy: privacy.nonPii }),
  DIM_LENGTH: z.number().nullish().meta({ privacy: privacy.nonPii }),
  DIM_WIDTH: z.number().nullish().meta({ privacy: privacy.nonPii }),
  DIM_WEIGHT: z.number().nullish().meta({ privacy: privacy.nonPii }),
  DIM_UNIT: z.string().nullish().meta({ privacy: privacy.nonPii }),
  ACTUAL_WEIGHT: z.number().nullish().meta({ privacy: privacy.nonPii }),
  ACTUAL_WEIGHT_UNIT: z.string().nullish().meta({ privacy: privacy.nonPii }),
  HEIGHT_IN_INCHES: z.number().nullish().meta({ privacy: privacy.nonPii }),
  LENGTH_IN_INCHES: z.number().nullish().meta({ privacy: privacy.nonPii }),
  WIDTH_IN_INCHES: z.number().nullish().meta({ privacy: privacy.nonPii }),
  WEIGHT_IN_OZ: z.number().nullish().meta({ privacy: privacy.nonPii }),
  DUTIES_AND_TAXES_1: z.number().nullish().meta({ privacy: privacy.nonPii }),
  DUTIES_AND_TAXES_2: z.number().nullish().meta({ privacy: privacy.nonPii }),
  DUTIES_AND_TAXES_3: z.number().nullish().meta({ privacy: privacy.nonPii }),
  DUTIES_AND_TAXES_4: z.number().nullish().meta({ privacy: privacy.nonPii }),
  DUTIES_AND_TAXES_5: z.number().nullish().meta({ privacy: privacy.nonPii }),
  NONSTANDARD_SURCHARGE: z.number().nullish().meta({ privacy: privacy.nonPii }),
  DELIVERY_AREA_SURCHARGE: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  MINIMUM_ORDER_PICKUP_FEE: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  DEMAND_SURCHARGE: z.number().nullish().meta({ privacy: privacy.nonPii }),
  FUEL_SURCHARGE: z.number().nullish().meta({ privacy: privacy.nonPii }),
  ACTUAL_LABEL_COST: z.number().meta({ privacy: privacy.nonPii }),
  PROVIDER_COST: z.number().meta({ privacy: privacy.nonPii }),
  BASE_CHARGE: z.number().meta({ privacy: privacy.nonPii }),
  LOADED_AT: z.date().meta({ privacy: privacy.nonPii }),
});
export const DhlAdjustmentSnowflakeResponseSchema: z.ZodType<DhlAdjustmentSnowflakeResponse> =
  dhlAdjustmentSnowflakeResponseSchema_;
export interface DhlAdjustmentSnowflakeResponse extends z.infer<
  typeof dhlAdjustmentSnowflakeResponseSchema_
> {}

export const baseSurchargeRowSchema = z.object({
  source: createEnum(SurchargeSource, { enumName: "SurchargeSource" }).meta({
    privacy: privacy.nonPii,
  }),
  createdAt: z.date().meta({ privacy: privacy.nonPii }),
  lastAttemptedAt: z.date().meta({ privacy: privacy.nonPii }),
  accountLevel: z.boolean().nullish().meta({ privacy: privacy.nonPii }),
});

const easypostSurchargeRowSchema = baseSurchargeRowSchema.extend({
  source: z.literal(SurchargeSource.Easypost).meta({ privacy: privacy.nonPii }),
  row: easypostSurchargeSnowflakeResponseSchema_.meta({
    privacy: privacy.structured,
  }),
});

const fedexSurchargeRowSchema = baseSurchargeRowSchema.extend({
  source: z.literal(SurchargeSource.Fedex).meta({ privacy: privacy.nonPii }),
  row: fedexSurchargeSnowflakeResponseSchema_.meta({
    privacy: privacy.structured,
  }),
});

const upsSurchargeRowSchema = baseSurchargeRowSchema.extend({
  source: z
    .literal(SurchargeSource.Unishippers)
    .meta({ privacy: privacy.nonPii }),
  row: upsAdjustmentSnowflakeResponseSchema_.meta({
    privacy: privacy.structured,
  }),
});

const amazonSurchargeRowSchema = baseSurchargeRowSchema.extend({
  source: z.literal(SurchargeSource.Amazon).meta({ privacy: privacy.nonPii }),
  row: amazonAdjustmentSnowflakeResponseSchema_.meta({
    privacy: privacy.structured,
  }),
});

const ibSurchargeRowSchema = baseSurchargeRowSchema.extend({
  source: z.literal(SurchargeSource.IB).meta({ privacy: privacy.nonPii }),
  row: ibAdjustmentSnowflakeResponseSchema_.meta({
    privacy: privacy.structured,
  }),
});

const firstMileSurchargeRowSchema = baseSurchargeRowSchema.extend({
  source: z
    .literal(SurchargeSource.FirstMile)
    .meta({ privacy: privacy.nonPii }),
  row: firstMileSurchargeSnowflakeResponseSchema_.meta({
    privacy: privacy.structured,
  }),
});

const upsDirectLabelSurchargeRowSchema = baseSurchargeRowSchema.extend({
  source: z
    .literal(SurchargeSource.UpsDirect)
    .meta({ privacy: privacy.nonPii }),
  accountLevel: z.literal(false).nullish().meta({ privacy: privacy.nonPii }),
  row: upsDirectLabelSurchargeSnowflakeResponseSchema_.meta({
    privacy: privacy.structured,
  }),
});

const upsDirectAccountLevelSurchargeRowSchema = baseSurchargeRowSchema.extend({
  source: z
    .literal(SurchargeSource.UpsDirect)
    .meta({ privacy: privacy.nonPii }),
  accountLevel: z.literal(true).meta({ privacy: privacy.nonPii }),
  row: upsDirectAccountLevelSurchargeSnowflakeResponseSchema_.meta({
    privacy: privacy.structured,
  }),
});

const upsDirectSurchargeRowSchema = z.union([
  upsDirectLabelSurchargeRowSchema,
  upsDirectAccountLevelSurchargeRowSchema,
]);

const uspsSurchargeRowSchema = baseSurchargeRowSchema.extend({
  source: z.literal(SurchargeSource.Usps).meta({ privacy: privacy.nonPii }),
  row: uspsAdjustmentSnowflakeResponseSchema_.meta({
    privacy: privacy.structured,
  }),
});

const dhlSurchargeRowSchema = baseSurchargeRowSchema.extend({
  source: z.literal(SurchargeSource.Dhl).meta({ privacy: privacy.nonPii }),
  row: dhlAdjustmentSnowflakeResponseSchema_.meta({
    privacy: privacy.structured,
  }),
});

const surchargeRowSchema_ = z.union([
  easypostSurchargeRowSchema,
  fedexSurchargeRowSchema,
  upsSurchargeRowSchema,
  amazonSurchargeRowSchema,
  ibSurchargeRowSchema,
  firstMileSurchargeRowSchema,
  upsDirectSurchargeRowSchema,
  uspsSurchargeRowSchema,
  dhlSurchargeRowSchema,
]);
export const surchargeRowSchema: z.ZodType<SurchargeRow> = surchargeRowSchema_;
export type SurchargeRow = z.infer<typeof surchargeRowSchema_>;
